import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { globalData } from './globaldata';
import { RUNTIME_ENV } from '../tokens/runtime_env.token';

declare global {
    interface Window {
        Microsoft?: any;
    }
}

interface AppInsightsConfig {
    instrumentationKey?: string;
    connectionString?: string;
    applicationId?: string;
    appEnv: string;
    scriptUrl: string;
}

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
    private readonly config: AppInsightsConfig;
    private appInsights: any;
    private initPromise?: Promise<void>;
    private scriptPromise?: Promise<void>;

    constructor(
        @Inject(RUNTIME_ENV) runtimeEnv: any,
        private readonly globalData: globalData
    ) {
        this.config = this.resolveConfig(runtimeEnv);
        if (this.hasTelemetryConfig()) {
            this.initPromise = this.initializeClient();
        }
    }

    trackPageView(pageName: string, url: string): void {
        if (!this.hasTelemetryConfig() || typeof window === 'undefined') {
            return;
        }

        const sendTelemetry = () => {
            if (!this.appInsights) {
                return;
            }

            const userId = this.resolveUserId();

            if (userId && typeof this.appInsights.setAuthenticatedUserContext === 'function') {
                this.appInsights.setAuthenticatedUserContext(userId);
            } else if (typeof this.appInsights.clearAuthenticatedUserContext === 'function') {
                this.appInsights.clearAuthenticatedUserContext();
            }

            const properties = this.buildTelemetryProperties();

            this.appInsights.trackPageView({
                name: pageName,
                uri: url,
                properties
            });
        };

        if (this.appInsights) {
            sendTelemetry();
            return;
        }

        if (!this.initPromise) {
            this.initPromise = this.initializeClient();
        }

        this.initPromise
            ?.then(() => sendTelemetry())
            .catch(() => undefined);
    }

    trackEvent(eventName: string, properties: Record<string, string> = {}): void {
        const normalizedEventName = this.resolveStringContextValue(eventName);
        if (!normalizedEventName || !this.hasTelemetryConfig() || typeof window === 'undefined') {
            return;
        }

        const sendTelemetry = () => {
            if (!this.appInsights || typeof this.appInsights.trackEvent !== 'function') {
                return;
            }

            this.appInsights.trackEvent(
                { name: normalizedEventName },
                {
                    ...properties,
                    ...this.buildTelemetryProperties()
                }
            );
        };

        if (this.appInsights) {
            sendTelemetry();
            return;
        }

        if (!this.initPromise) {
            this.initPromise = this.initializeClient();
        }

        this.initPromise
            ?.then(() => sendTelemetry())
            .catch(() => undefined);
    }

    private buildTelemetryProperties(): Record<string, string> {
        const userId = this.resolveUserId();
        const userEmail = this.resolveStringContextValue(this.globalData.email);
        const accountName = this.resolveAccountName();
        const properties: Record<string, string> = {
            userId: userId ?? 'anonymous',
            userEmail: userEmail ?? '',
            accountName: accountName ?? '',
            appEnv: this.config.appEnv,
            timestamp: new Date().toISOString()
        };

        if (this.config.applicationId) {
            properties.applicationId = this.config.applicationId;
        }

        return properties;
    }

    private resolveUserId(): string | undefined {
        const { userId } = this.globalData;
        if (userId === null || userId === undefined) {
            return undefined;
        }
        return String(userId);
    }

    private resolveAccountName(): string | undefined {
        return this.resolveFirstStringContextValue(
            this.globalData.account_name,
            this.globalData.accountName,
            this.globalData.company_name
        );
    }

    private resolveFirstStringContextValue(...values: unknown[]): string | undefined {
        for (const value of values) {
            const normalized = this.resolveStringContextValue(value);
            if (normalized) {
                return normalized;
            }
        }

        return undefined;
    }

    private resolveStringContextValue(value: unknown): string | undefined {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed || undefined;
        }

        if (value === null || value === undefined) {
            return undefined;
        }

        const normalized = String(value).trim();
        return normalized || undefined;
    }

    private hasTelemetryConfig(): boolean {
        return !!(this.config.instrumentationKey || this.config.connectionString);
    }

    private initializeClient(): Promise<void> {
        if (!this.hasTelemetryConfig() || typeof window === 'undefined') {
            return Promise.resolve();
        }

        return this.ensureScriptLoaded()
            .then(() => {
                const appInsightsCtor = window.Microsoft?.ApplicationInsights?.ApplicationInsights;

                if (!appInsightsCtor) {
                    return;
                }

                this.appInsights = new appInsightsCtor({
                    config: {
                        instrumentationKey: this.config.instrumentationKey,
                        connectionString: this.config.connectionString,
                        enableAutoRouteTracking: false
                    }
                });

                if (typeof this.appInsights.loadAppInsights === 'function') {
                    this.appInsights.loadAppInsights();
                }
            })
            .catch(() => undefined);
    }

    private ensureScriptLoaded(): Promise<void> {
        if (typeof window === 'undefined') {
            return Promise.resolve();
        }

        if ((window as any).Microsoft?.ApplicationInsights?.ApplicationInsights) {
            return Promise.resolve();
        }

        if (this.scriptPromise) {
            return this.scriptPromise;
        }

        this.scriptPromise = new Promise<void>((resolve) => {
            const script = document.createElement('script');
            script.src = this.config.scriptUrl;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => resolve();
            document.head.appendChild(script);
        });

        return this.scriptPromise;
    }

    private resolveConfig(runtimeEnv: any): AppInsightsConfig {
        const runtimeAppInsights = runtimeEnv?.appInsights ?? {};

        const instrumentationKey =
            runtimeAppInsights.instrumentationKey ??
            runtimeEnv?.APPINSIGHTS_INSTRUMENTATION_KEY ??
            runtimeEnv?.appInsightsInstrumentationKey ??
            environment.appInsights?.instrumentationKey;

        const connectionString =
            runtimeAppInsights.connectionString ??
            runtimeEnv?.APPINSIGHTS_CONNECTION_STRING ??
            runtimeEnv?.appInsightsConnectionString ??
            environment.appInsights?.connectionString;

        const applicationId =
            runtimeAppInsights.applicationId ??
            runtimeEnv?.APPINSIGHTS_APPLICATION_ID ??
            runtimeEnv?.appInsightsApplicationId ??
            environment.appInsights?.applicationId;

        const appEnv =
            runtimeAppInsights.appEnv ??
            runtimeEnv?.APP_ENV ??
            runtimeEnv?.appEnv ??
            environment.appEnv ??
            'local';

        const scriptUrl =
            runtimeAppInsights.scriptUrl ??
            runtimeEnv?.APPINSIGHTS_SCRIPT_URL ??
            'https://js.monitor.azure.com/scripts/b/ai.2.min.js';

        return {
            instrumentationKey,
            connectionString,
            applicationId,
            appEnv,
            scriptUrl
        };
    }
}
